/**
 * AI review picker. Premium-gated, free users see a paywall stub.
 *
 * Per-card state (computed from /ai/eligibility):
 *   A. Never generated → "Generate →"
 *   B. Has review at current profile_version → "View last review →" only,
 *      with a hint "Update your profile to generate a new one."
 *   C. Has review at older profile_version → BOTH "View last review →"
 *      AND "Generate fresh →".
 *   D. Free tier → 🔒 Premium chip + paywall modal stub.
 *
 * In-flight gate (any pending/streaming review across all personalities)
 * disables every Generate button with a "Review in progress" hint.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  aireviewApi,
  sessionStore,
  type AIPersonalityMeta,
  type AIPersonalityEligibility,
  type User,
  api,
  APIError,
} from '@/lib/api';
import { AppTopbar } from '@/components';
import { PaywallSheet } from '@/components/PaywallSheet';
import { friendlyError } from '@/lib/errors';

export function AIPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(sessionStore.getUser());
  const [personalities, setPersonalities] = useState<AIPersonalityMeta[]>([]);
  const [eligibility, setEligibility] = useState<AIPersonalityEligibility[]>([]);
  const [inFlightID, setInFlightID] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paywall, setPaywall] = useState<AIPersonalityMeta | null>(null);

  const loadAll = useCallback(async () => {
    setError(null);
    try {
      // Personalities is public, eligibility is authed — kicked off in
      // parallel since they don't depend on each other.
      const [p, e] = await Promise.all([
        aireviewApi.personalities(),
        aireviewApi.eligibility().catch((err) => {
          // Free-tier users still see the picker (so they can see what's
          // behind the paywall). eligibility() succeeds for them too —
          // can_generate just reflects the underlying profile gate. We
          // only swallow when the user isn't even authed.
          if (err instanceof APIError && err.status === 401) return null;
          throw err;
        }),
      ]);
      setPersonalities(p.personalities);
      if (e) {
        setEligibility(e.personalities);
        setInFlightID(e.in_flight_review_id ?? null);
      }
    } catch (e) {
      setError(friendlyError(e, 'AI'));
    }
  }, []);

  useEffect(() => {
    // AppShell handles the auth gate + /auth/me load; we still refetch
    // here so the tier reflects an in-session upgrade without a page
    // reload (the cached user from session may pre-date checkout).
    api.me().then(setUser).catch(() => null);
    loadAll();
  }, [loadAll]);

  const onGenerate = async (p: AIPersonalityMeta) => {
    if (user?.tier !== 'premium') {
      setPaywall(p);
      return;
    }
    try {
      const created = await aireviewApi.create(p.id);
      navigate(`/ai/${created.id}`);
    } catch (e) {
      if (e instanceof APIError && e.status === 409) {
        const details = e.details as { existing_review_id?: string } | undefined;
        if (details?.existing_review_id) {
          navigate(`/ai/${details.existing_review_id}`);
          return;
        }
      }
      setError(friendlyError(e, 'AI'));
    }
  };

  return (
    <>
      <AppTopbar
        eyebrow={user?.tier === 'premium' ? 'AI reviewers · Unlocked' : 'AI reviewers · Premium'}
        title={<>Get a <span style={{ fontStyle: 'italic' }}>second opinion</span>.</>}
      />
      <main style={{ padding: '0 40px 60px', maxWidth: 1080 }}>

      {error && <div style={errorBox}>{error}</div>}

      {inFlightID && (
        <div style={infoBox}>
          You have a review in progress. <Link to={`/ai/${inFlightID}`} style={inlineLink}>Open it →</Link>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 18 }}>
        {personalities.map((p) => {
          const elig = eligibility.find((e) => e.id === p.id);
          const isPremium = user?.tier === 'premium';
          const lastReviewID = elig?.last_review_id;
          const lastReviewAt = elig?.last_review_at;
          // can_generate is the server's source of truth (it's the
          // result of CheckEligibility). But we also need to gate on
          // user.tier client-side so the paywall modal fires correctly.
          const canGenerate = (elig?.can_generate ?? true) && !inFlightID;
          const lockedByProfile = elig?.reason === 'profile_unchanged';

          return (
            <div key={p.id} style={card}>
              <div className="mono" style={{ fontSize: 11, color: 'var(--coral)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>
                Personality
              </div>
              <h2 className="serif" style={{ fontSize: 26, fontStyle: 'italic', fontWeight: 500, lineHeight: 1.1, marginBottom: 10 }}>
                {p.display_name}
              </h2>
              <p style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.5, marginBottom: 18 }}>
                {p.tagline}
              </p>

              {/* Meta strip: "last X ago" or "not yet" */}
              <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 14 }}>
                {lastReviewAt
                  ? `Last: ${new Date(lastReviewAt).toLocaleDateString()}`
                  : 'Not yet'}
              </div>

              {/* Actions: at most two buttons, stacked. */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {lastReviewID && (
                  <Link to={`/ai/${lastReviewID}`} style={btnSecondary}>
                    View last review →
                  </Link>
                )}
                {/* Free-tier users see a normal "Generate" button to keep
                    the surface inviting; tapping it opens the paywall
                    sheet instead of starting a review. */}
                <button
                  onClick={() => onGenerate(p)}
                  disabled={isPremium && !canGenerate}
                  style={isPremium && !canGenerate ? btnPrimaryDisabled : btnPrimary}
                  title={isPremium && lockedByProfile
                    ? "Update your profile to generate a new one."
                    : isPremium && inFlightID
                      ? "Wait for the in-flight review to finish."
                      : undefined}
                >
                  {lastReviewID ? 'Generate fresh →' : 'Generate →'}
                </button>
              </div>

              {/* Tail hint — only when we're locked specifically by the
                  staleness gate (not when we're also offering Generate). */}
              {lockedByProfile && !canGenerate && (
                <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 12, lineHeight: 1.5 }}>
                  Update your profile to generate a new one.
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Paywall — single shared sheet for the whole app. Sheet handles
          plan fetch + Stripe checkout redirect; we just flip `open`. */}
      <PaywallSheet
        open={paywall !== null}
        trigger="ai_review"
        onClose={() => setPaywall(null)}
      />
      </main>
    </>
  );
}

const card: React.CSSProperties = {
  background: 'var(--surface)',
  borderRadius: 22,
  padding: 24,
  boxShadow: 'var(--shadow-card)',
};

const inlineLink: React.CSSProperties = {
  color: 'var(--coral)',
  fontWeight: 600,
  textDecoration: 'none',
};

const btnPrimary: React.CSSProperties = {
  display: 'inline-block',
  textAlign: 'center',
  background: 'var(--coral)',
  color: '#fff',
  border: 'none',
  borderRadius: 100,
  padding: '10px 20px',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
  textDecoration: 'none',
  fontFamily: 'var(--font-ui)',
};

const btnPrimaryDisabled: React.CSSProperties = {
  ...btnPrimary,
  opacity: 0.5,
  cursor: 'not-allowed',
};

const btnSecondary: React.CSSProperties = {
  display: 'inline-block',
  textAlign: 'center',
  background: 'transparent',
  color: 'var(--ink)',
  border: '1px solid var(--line-strong)',
  borderRadius: 100,
  padding: '9px 20px',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
  textDecoration: 'none',
  fontFamily: 'var(--font-ui)',
};

const errorBox: React.CSSProperties = {
  background: 'rgba(208,64,48,0.08)',
  color: '#a8331f',
  border: '1px solid rgba(208,64,48,0.2)',
  borderRadius: 12,
  padding: '10px 14px',
  fontSize: 13,
  marginBottom: 20,
};

const infoBox: React.CSSProperties = {
  background: 'rgba(43, 29, 20, 0.04)',
  color: 'var(--ink-2)',
  border: '1px solid var(--line)',
  borderRadius: 12,
  padding: '12px 16px',
  fontSize: 14,
  marginBottom: 24,
};

